<!--
 * COPYRIGHT Transtron Inc 2022
 *
 * 機能名：エリア別分析画面
 *
 * 作成者：2021/11/30 ノア）黄
 * 最終修正者：2022/02/12 ノア）虞
 *
 -->
<template>
  <div class="tti-main-view">
    <!-- ヘッダー -->
    <section>
      <nav class="tti-main-header-shadow">
        <div class="pull-left">
          <el-button
            class="backButton"
            @click="onBackButtonClick"
            v-tooltip="{
              content: `${$t('master.tooltipCommon.back')}`,
              placement: 'bottom-start',
              delay: { show: 1000, hide: 0 }
            }"
          >
            <tti-svg-icon icon-name="wo_034"></tti-svg-icon>
          </el-button>
          <span class="tti-viewTitle">
            <tti-svg-icon icon-name="wo_012"></tti-svg-icon>
            {{ $t('operationalPerformance.analysisByAreaTitle') }}
          </span>
        </div>
        <div class="pull-right">
          <!-- 管理対象分析 -->
          <el-button
            class="tti-btn-jump"
            @click="onManagementObjectAnalysisClick"
            v-tooltip="{
              content: `${$t('operationalPerformance.managementObjectAnalysisTip')}`,
              placement: 'bottom-start',
              delay: { show: 1000, hide: 0 }
            }"
          >
            <tti-svg-icon icon-name="wo_012"></tti-svg-icon>
            {{ $t('operationalPerformance.managementObjectAnalysis', [managementTargetShowName]) }}
            <tti-svg-icon icon-name="wo_035"></tti-svg-icon>
          </el-button>
          <!-- グラフ表示 -->
          <el-button
            class="tti-btn-jump mar-8"
            @click="onShowGraphClick"
            v-tooltip="{
              content: `${$t('operationalPerformance.showGraphTip')}`,
              placement: 'bottom-start',
              delay: { show: 1000, hide: 0 }
            }"
          >
            <tti-svg-icon icon-name="wo_016"></tti-svg-icon>
            {{ $t('operationalPerformance.showGraph') }}
            <tti-svg-icon icon-name="wo_035"></tti-svg-icon>
          </el-button>
        </div>
      </nav>
    </section>
    <!-- テーブル -->
    <section class="tti-padding-content">
      <div class="tti-lr-container">
        <!-- 選択済エリア -->
        <div class="left tti-tree">
          <div class="tti-content-title">
            <span class="contentTitle">{{ $t('operationalPerformance.choosedArea') }}</span>
            <el-popover
              placement="bottom"
              popper-class="tti-area-popover"
              width="400"
              trigger="click"
              @hide="onAreaSelected"
            >
              <el-button
                class="tti-btn-default tti-btn-005 tti-btn-title"
                slot="reference"
                v-tooltip="{
                  content: `${$t('operationalPerformance.areaSelectTip')}`,
                  placement: 'bottom-start',
                  delay: { show: 1000, hide: 0 }
                }"
                >{{ $t('operationalPerformance.areaSelect') }}</el-button
              >
              <el-tree
                :data="areaOptions"
                show-checkbox
                node-key="id"
                default-expand-all
                :check-strictly="true"
                ref="areaTree"
              >
              </el-tree>
            </el-popover>
          </div>
          <div class="content">
            <el-tree :data="selectedAreaOptions" node-key="id" :empty-text="$t('messages.common.emptyTableData')">
            </el-tree>
          </div>
        </div>
        <!-- 集計期間選択 -->
        <div class="right tti-tree">
          <div class="tti-content-title">
            <span>{{ $t('operationalPerformance.aggregateSelection') }}</span>
          </div>
          <div class="content content-height">
            <el-form :model="aggregateSelection" label-width="100px" class="aggregateSelectionForm">
              <el-form-item prop="phoneNumber" :label="$t('operationalPerformance.operationStartDate')">
                <el-date-picker
                  v-model="aggregateSelection.operationStartDay"
                  type="date"
                  size="small"
                  format="yyyy/MM/dd"
                  value-format="yyyy/MM/dd"
                  :picker-options="pickerOptions"
                  :editable="false"
                  :clearable="false"
                  v-tooltip="{
                    content: `${$t('master.tooltipCommon.operationStartDayTip')}`,
                    placement: 'bottom-start',
                    delay: { show: 1000, hide: 0 }
                  }"
                ></el-date-picker>
              </el-form-item>
              <el-form-item prop="name" :label="$t('operationalPerformance.operationEndDate')">
                <el-date-picker
                  v-model="aggregateSelection.operationEndDay"
                  type="date"
                  size="small"
                  format="yyyy/MM/dd"
                  value-format="yyyy/MM/dd"
                  :picker-options="pickerOptions"
                  :editable="false"
                  :clearable="false"
                  v-tooltip="{
                    content: `${$t('master.tooltipCommon.operationEndDayTip')}`,
                    placement: 'bottom-start',
                    delay: { show: 1000, hide: 0 }
                  }"
                ></el-date-picker>
              </el-form-item>
            </el-form>
          </div>
          <!-- 実績データ選択 -->
          <div class="tti-content-title">
            <span>{{ $t('operationalPerformance.actualDataSelection') }}</span>
          </div>
          <div class="content content-height">
            <el-checkbox
              class="displayDeleteDataCheckbox"
              v-model="isDeleteDataDisplay"
              v-tooltip="{
                content: `${$t('operationalPerformance.deleteDataDisplayTip')}`,
                placement: 'bottom-start',
                delay: { show: 1000, hide: 0 }
              }"
            >
              {{ $t('operationalPerformance.deleteDataDisplayTip') }}
            </el-checkbox>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator'
import { Tree } from 'element-ui'
import { AxiosResponse } from 'axios'

import { i18n } from '@/locales'
import { msgBoxService } from '@/commons/services/msgBoxService'
import { TreeData, AggregateSelection, PrefectureAllocData, ResponseData } from '@/models'
import { AreaRole, DeleteType } from '@/commons/constants'
import { getAreaMasterDataWhoHasSubPrefecture, opPerfAreaManageCSVDownload } from '@/store/apis'
import config from '@/config/config'
import { changeBlobToJson } from '@/store/apis/apiClient'
import HelperService from '@/service/helperService'
import { localService } from '@/commons/services/localService'
import { isDateAfter, isDateDayOver, pickerOptions } from '@/utils/dateUtils'

/**
 * エリア別分析画面
 *
 * @class
 */
@Component({
  name: 'op-perf-area'
})
export default class OP_PERF_AREA extends Vue {
  /**
   * タイムセレクタをクリックしたときの処理
   */
  pickerOptions = pickerOptions

  /**
   * 削除済も表示する
   */
  isDeleteDataDisplay = false

  /**
   * エリア情報
   */
  areaOptions: TreeData[] = []

  /**
   * 選択中のエリアリスト
   */
  selectedAreaOptions: TreeData[] = []

  /**
   * 集計期間情報
   */
  aggregateSelection: AggregateSelection = {
    operationStartDay: '',
    operationEndDay: ''
  }

  /**
   * 管理対象表示名
   *
   * @returns {string} 管理対象表示名
   */
  get managementTargetShowName(): string {
    return localService.userInfo?.managementTargetShowName
  }

  /**
   * インスタンスがマウントされたちょうど後にフック処理
   *
   * @returns {void}
   */
  mounted(): void {
    this.$log.debug('OP_PERF_AREA > mounted')
    this.fetchAreaOptions()
  }

  /**
   * エリア情報を取得する
   *
   * @returns {void}
   */
  async fetchAreaOptions(): Promise<void> {
    const data = (await getAreaMasterDataWhoHasSubPrefecture()) as PrefectureAllocData
    this.changePrefectureAllocDataToTree(data)
    this.$log.debug('OP_PERF_AREA > fetchAreaOptions', data)
    this.resetSelectedAreaOptions()
  }

  /**
   * エリアマスタをTreeDataに変換する
   *
   * @param {PrefectureAllocData} data エリアマスタ
   * @returns {void}
   */
  changePrefectureAllocDataToTree(data: PrefectureAllocData): void {
    this.$log.debug('OP_PERF_AREA > changeAreaMasterToTree')
    this.areaOptions = data.areaList.map(item => {
      return {
        id: item.id,
        label: item.areaName,
        expand: true,
        isLastNode: false,
        level: AreaRole.AREA,
        children: item.prefectureList?.map(prefecture => {
          return {
            id: prefecture.id,
            label: prefecture.prefectureName,
            level: AreaRole.PREFECTURE,
            isLastNode: true,
            children: []
          }
        })
      }
    })
  }

  /**
   * 選択したエリアをリセットする
   *
   * @returns {void}
   */
  resetSelectedAreaOptions(): void {
    ;(this.$refs.areaTree as Tree).setCheckedKeys([])
    this.onAreaSelected()
  }

  /**
   * 選択中のエリア情報を変更した時の処理
   *
   * @returns {void}
   */
  onAreaSelected(): void {
    const selectedArea = (this.$refs.areaTree as Tree).getCheckedNodes() as TreeData[]
    // エリア選択で、階層レベルの異なるエリアを選択するとエラーとする。
    if (HelperService.isAreaLevelDifferent(selectedArea)) {
      msgBoxService.error(i18n.t('messages.areaSelectErr').toString())
      return
    }

    this.selectedAreaOptions = []
    this.selectedAreaOptions = selectedArea.map((item: TreeData) => {
      return { id: item.id, label: item.label, level: item.level }
    })
    this.$log.debug('OP_PERF_AREA > onAreaSelected', this.selectedAreaOptions)
  }

  /**
   * 戻るボタンをクリックした時の処理
   *
   * @returns {void}
   */
  onBackButtonClick(): void {
    window.history.back()
    this.$log.debug('OP_PERF_AREA > onBackButtonClick')
  }

  /**
   * APIを呼び出すときに、エリアが存在しない可能性があります、これらのAPIをプロキシする。
   *
   * @param {any} target プロキシの目標処理
   * @returns {Promise<void>}
   */
  // eslint-disable-next-line
  async areaNotExistProxy(target: any): Promise<void> {
    try {
      await target()
    } catch (e) {
      this.$log.debug('OP_PERF_AREA > areaNotExistProxy: e=', e)
      const err = e as AxiosResponse<ResponseData>
      // エリアが存在しない場合、都道府県割当一覧から一覧情報を再取得する。
      changeBlobToJson(err).then(commonResponse => {
        if (commonResponse.common.code === 'E00091') {
          this.fetchAreaOptions()
        }
      })
      throw e
    }
  }

  /**
   * グラフ表示ボタンをクリックした時の処理
   *
   * @returns {void}
   */
  async onShowGraphClick(): Promise<void> {
    setTimeout(() => {
      if (!this.correlationCheck()) return
      this.handleJump()
    })
  }

  /**
   * グラフ表示ジャンプ
   *
   * @returns {void}
   */
  handleJump(): void {
    const level = this.selectedAreaOptions[0]?.level
    if (level === AreaRole.AREA) {
      this.$router.push({
        path: config.menu.chartArea,
        query: {
          areaIdList: this.selectedAreaOptions.map(item => item.id),
          operationStartDate: this.aggregateSelection.operationStartDay,
          operationEndDate: this.aggregateSelection.operationEndDay,
          deleteFlg: this.isDeleteDataDisplay ? DeleteType.deleteData : DeleteType.notdeleteData
        }
      })
    }

    if (level === AreaRole.PREFECTURE) {
      this.$router.push({
        path: config.menu.chartPrefecture,
        query: {
          level: AreaRole.PREFECTURE,
          prefectureIdList: this.selectedAreaOptions.map(item => item.id),
          operationStartDate: this.aggregateSelection.operationStartDay,
          operationEndDate: this.aggregateSelection.operationEndDay,
          deleteFlg: this.isDeleteDataDisplay ? DeleteType.deleteData : DeleteType.notdeleteData
        }
      })
    }
  }

  /**
   * 管理対象分析ボタンをクリックした時の処理
   *
   * @returns {void}
   */
  onManagementObjectAnalysisClick(): void {
    if (!this.correlationCheck()) return

    const confirmCallback = async () => {
      this.areaNotExistProxy(this.managementCsvOutputAPI)
    }
    msgBoxService.confirm(i18n.t('operationalPerformance.I00160').toString(), confirmCallback)
    this.$log.debug('OP_PERF_AREA > onManagementObjectAnalysisClick')
  }

  /**
   * 管理対象分析CSV出力API
   *
   * @returns {void}
   */
  async managementCsvOutputAPI(): Promise<void> {
    let areaLevel = 0
    const level = this.selectedAreaOptions[0]?.level
    if (level === AreaRole.AREA) {
      areaLevel = 0
    }

    if (level === AreaRole.PREFECTURE) {
      areaLevel = 1
    }

    const params = {
      areaIdList: this.selectedAreaOptions.map(item => item.id),
      operationStartDate: this.aggregateSelection.operationStartDay,
      operationEndDate: this.aggregateSelection.operationEndDay,
      areaLevel: areaLevel,
      deleteFlg: this.isDeleteDataDisplay ? DeleteType.deleteData : DeleteType.notdeleteData
    }
    const res = await opPerfAreaManageCSVDownload(params)
    HelperService.download(res.fileName, res.data)
  }

  /**
   * 相関チェックを行う
   *
   * @returns {boolean} 相関チェック結果
   */
  correlationCheck(): boolean {
    if (this.selectedAreaOptions.length === 0) {
      msgBoxService.info(i18n.t('vaildate.common.selectRequired', [i18n.t('operationalPerformance.area')]).toString())
      return false
    }

    if (!this.aggregateSelection.operationStartDay) {
      msgBoxService.info(
        i18n.t('vaildate.common.selectRequired', [i18n.t('operationalPerformance.operationStartDate')]).toString()
      )
      return false
    }

    if (!this.aggregateSelection.operationEndDay) {
      msgBoxService.info(
        i18n.t('vaildate.common.selectRequired', [i18n.t('operationalPerformance.operationEndDate')]).toString()
      )
      return false
    }

    if (isDateAfter(this.aggregateSelection.operationStartDay, this.aggregateSelection.operationEndDay)) {
      msgBoxService.info(
        i18n
          .t('vaildate.common.dateRange', [
            i18n.t('operationalPerformance.operationStartDate'),
            i18n.t('operationalPerformance.operationEndDate')
          ])
          .toString()
      )
      return false
    }

    if (isDateDayOver(this.aggregateSelection.operationEndDay, this.aggregateSelection.operationStartDay, 31)) {
      msgBoxService.info(i18n.t('vaildate.common.differenceInDays', [31]).toString())
      return false
    }

    return true
  }
}
</script>

<style lang="scss" scoped>
::v-deep .el-popover {
  height: 500px !important;
  overflow: auto;
}

.tti-tree {
  overflow: auto;

  .tti-content-title {
    font-size: $FS02;
  }

  .content-height {
    height: 120px !important;
  }

  ::v-deep ul {
    padding-left: 24px;
  }

  ::v-deep .content {
    > ul {
      padding-left: 0;
      margin-top: 0;
      white-space: nowrap;
    }

    .el-tree {
      height: 100%;
    }
  }
}

::v-deep .el-tree-node .el-tree-node__content {
  background-color: #fff;
}

.aggregateSelectionForm {
  width: 300px;
}

.pull-left {
  display: flex;
}

.tti-btn-title {
  width: 100px;
  height: 30px;
}

.tti-main-header-shadow {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

.tti-btn-jump {
  background-color: $C43;
  height: 100%;
  float: right;
  padding: 0;

  .svg-icon {
    padding: 0 6px;
  }

  &:active {
    @include bt_001_p;
  }
}

.mar-8 {
  margin-right: 8px;
}
</style>
